/// @ts-strict-ignore
import { Connection } from '@3d-scene/model3d/connection';
import { ProjectModel } from '@project/model/project.model';
import { ITableProvider, TableProvider, TableProviderTabLabel, TableProviderType } from '@project/components/label/label-editor/table-provider';
import { IArticleSpanData, IConnectionSpanData, isArticleSpanData, isConnectionSpanData, ISpanData, SpanDataFactory } from '@project/components/label/label-editor/span-data';
import { InsertArticleHelper } from '@article/helper/insert-article.helper';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ArticleTypeConstants } from '@article/constants/article-type-constants';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { Color, Mesh } from 'three';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { Dictionary } from 'typescript-collections';
import { ArrayUtils } from '@shared/util/array-utils';
import { TextureConnectionInfo } from '@3d-scene/model3d/texture-connection-info';
import { ArticleUtils } from '@article/util/article-utils';
import { TextCheckSpacingData } from '@3d-scene/manager/text-check-spacing-data';
import { NumberUtils } from '@shared/util/number-utils';
import { ReferenceArticleUsageUtils } from '@article/util/reference-article-usage-utils';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { CompressionSimpleTextValueVO, SimpleTextValueVO, TableTextItemVO, TableTextRowVO, TableTextValueVO, TextureAreaUsageVO, TextureAreaVO, TextValueVO } from '@shared/vo/meta-element.vo';
import { DefaultTextParametersManager } from '@project/components/article/manager/default-text-parameters.manager';
import { PropertyUtils } from '@article/util/property-utils';
import { MetaElementBehaviorPropertyDef } from '@project/constants/meta-element-behavior-property-def';
import { LabelingUtils } from '@3d-scene/model3d/util/labeling-utils';
import { LabelFormat } from '@project/components/label/label-editor/label-format';
import { TextSizeCheckManager } from '@3d-scene/manager/text-size-check.manager';
import { TableProviderUtils } from '@project/components/label/label-editor/service/table-provider-utils';
import { TextureUtils } from '@3d-scene/model3d/util/texture-utils';
import { AdvancedLine } from '@3d-scene/model/advanced-line';
import { MathUtils } from '@3d-scene/common/util/math-utils';
import { ProjectContentPresenter } from '@shared/presenter/project-content.presenter';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { CompositeArticleUtils } from '@article/util/composite-article-utils';
import { LabelEditorTabComponent } from '../label-editor-tab/label-editor-tab.component';
import { TerminalBlockManager } from '@project/manager/terminal-block.manager';
import { LocaleUtils } from '@shared/util/locale-utils';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { Injectable } from '@angular/core';
import { InsertArticleHelperFactory } from '@article/helper/insert-article-helper.factory';
import { ProjectArticleService } from '@project/service/project-article.service';
import { ProjectMetaElementService } from '@project/manager/project-meta-element.service';
import { IsProjectType } from '@project/model/is-project-type';
import { CompositeArticleInfoUtils } from '@article/util/composite-article-info-utils';
import { ConfiguratorParameters } from '@3d-scene/configurator-parameters/configurator-parameters';
import { MeshUtils } from '@3d-scene/common/util/mesh-utils';
import { ArticleToolTipTextUtils } from '@article/manager/article-tooltip-text-utils';

class EndPlateInfo {
  public articleUsageId: string = null;
  public width: number = NaN;
  public spacing: number = NaN;

  constructor(articleUsageId: string) {
    this.articleUsageId = articleUsageId;
  }
}

class EndPlateFormat {
  public endPlates: EndPlateInfo[] = [];

  public get totalSpacing(): number {
    let result: number = 0;
    for (const endPlate of this.endPlates) {
      if (NumberUtils.isNumber(endPlate.spacing)) {
        result += endPlate.spacing;
      }
    }
    return result;
  }
}

class ConnectionFormat {
  public width: number = NaN;
  public height: number = NaN;
  public borderColor: string = null;
  public backgroundColor: string = null;
  public isMergeWithNext: boolean = false;
  public spacingToNextSpanDataInRow: number = 0;
}

class SeparationFormat {
  static readonly DEFAULT_WIDTH: number = 8;
  static readonly DEFAULT_BACKGROUND_COLOR: string = '#000000';

  public width: number = NaN;
  public height: number = NaN;
  public backgroundColor: string = null;
  public maxMultilineCount: number = NaN;
}

interface RowSpanData {
  spanData: ISpanData[][];
}

@Injectable({ providedIn: 'root' })
export class TableProviderGenerator {
  constructor(
    private readonly localResourceManager: LocaleResourceManager,
    private readonly projectModel: ProjectModel,
    private readonly projectArticleService: ProjectArticleService,
    private readonly projectMetaElementService: ProjectMetaElementService,
    private readonly textSizeCheckManager: TextSizeCheckManager,
    private readonly articleToolTipTextUtils: ArticleToolTipTextUtils,
    private readonly defaultTextParametersManager: DefaultTextParametersManager,
    private readonly presenter: ProjectContentPresenter,
    private readonly terminalBlockManager: TerminalBlockManager,
    private readonly containerService: ContainerService,
    private readonly labelingUtils: LabelingUtils,
    private readonly compositeArticleUtils: CompositeArticleUtils,
    private readonly compositeArticleInfoUtils: CompositeArticleInfoUtils,
    private readonly articleContainerUtils: ArticleContainerUtils,
    private readonly insertArticleHelperFactory: InsertArticleHelperFactory,
    private readonly spanDataFactory: SpanDataFactory,
  ) {}

  public generate(selectedConnectionCubes: Connection[], type: TableProviderType = TableProviderType.DEFAULT): ITableProvider {
    let rows: ISpanData[][] = [];
    const lineSpans: number[] = [];

    let textureCubes: Connection[] = this.getTextureCubesBySelectedConnections(selectedConnectionCubes);
    const corridorPositions: number[] = this.labelingUtils.getCorridorsByConnectionCubes(selectedConnectionCubes);
    const insertArticleHelper: InsertArticleHelper = this.createInsertArticleHelper();
    for (const pos of corridorPositions) {
      const textureCubesInCorridor: Connection[] = this.labelingUtils.getConnectionCubesInCorridor(textureCubes, pos);
      textureCubes = textureCubes.filter(ArrayUtils.notInArray, textureCubesInCorridor);
      const cubeIdToEndPlateSpacing: Dictionary<string, EndPlateFormat> = this.getEndPlateSpacingAfterCubes(
        textureCubesInCorridor,
        selectedConnectionCubes,
        insertArticleHelper
      );

      const selectedTextureCubesInCorridor: Connection[] = textureCubesInCorridor.filter((c: Connection) => selectedConnectionCubes.indexOf(c) >= 0);
      const baserowDistance: number = TableProviderUtils.calculateLineSpan(selectedTextureCubesInCorridor);
      const rowSpanDatas: ISpanData[][] = this.createSpanDataInCorridor(selectedTextureCubesInCorridor, baserowDistance, cubeIdToEndPlateSpacing);
      rows = rows.concat(rowSpanDatas);
      lineSpans.push(baserowDistance);
    }

    if (type !== TableProviderType.DIRECT_PRINTING) {
      rows = this.addBlankAndSeparationCells(rows, lineSpans);
    }
    let columnHeaderTooltips: string[] = null;
    if (type !== TableProviderType.SEPARATE_LABELING && type !== TableProviderType.DIRECT_PRINTING) {
      columnHeaderTooltips = this.createColumnHeaderTooltips(rows);
    }
    return new TableProvider(this.containerService, type, rows, lineSpans, columnHeaderTooltips);
  }

  public generateGrouped(selectedConnectionCubes: Connection[]): ITableProvider[] {
    if (ArrayUtils.isEmpty(selectedConnectionCubes)) {
      return [new TableProvider(this.containerService)];
    }
    const tableProviders: ITableProvider[] = [];
    const connectionCubesToProcess: Connection[] = selectedConnectionCubes.slice();
    ArrayUtils.addAll(tableProviders, this.generateDirectPrinting(connectionCubesToProcess));
    ArrayUtils.addAll(tableProviders, this.generateGroupedBySeparateLabeling(connectionCubesToProcess));
    ArrayUtils.addAll(tableProviders, this.generateGroupedByLabelingCategory(connectionCubesToProcess));
    tableProviders.sort((a: ITableProvider, b: ITableProvider) => TableProviderUtils.sortTableProvidersByPosition(a, b));
    return tableProviders;
  }

  public generateDirectPrinting(connectionCubes: Connection[]): ITableProvider[] {
    const tableProviders: ITableProvider[] = [];
    const directPrintingConnectionCubes: Connection[] = connectionCubes.filter((connectionCube) => {
      return !ArticleUtils.isLabelingArticle(connectionCube.article);
    });
    if (ArrayUtils.isNotEmpty(directPrintingConnectionCubes)) {
      ArrayUtils.removeAllItemsInArray(connectionCubes, directPrintingConnectionCubes);
      const tableProvider: ITableProvider = this.generate(directPrintingConnectionCubes, TableProviderType.DIRECT_PRINTING);
      this.generateDirectPrintingDescriptions(tableProvider.table);
      tableProvider.tabLabel = {
        label: 'labelEditorPopup.label.direct_printing_tab_label',
      };
      tableProviders.push(tableProvider);
    }
    return tableProviders;
  }

  private generateDirectPrintingDescriptions(table: ISpanData[][]): void {
    for (const row of table) {
      for (const spanData of row) {
        if (isConnectionSpanData(spanData) && !spanData.isEndPlate) {
          spanData.description = this.generateDirectPrintingCellDescription(spanData);
        }
      }
    }
  }

  private generateDirectPrintingCellDescription(spanData: IConnectionSpanData): string {
    const articleContainer: ArticleContainer = this.findArticleContainerForHeader(spanData);
    const articlePosition: string = this.presenter.getPositionLabelByArticleUsageId(articleContainer.articleUsage.id);
    let articleNo: string = articleContainer.article.articleNo;
    if (this.compositeArticleInfoUtils.isCompositePartArticle(articleContainer)) {
      articleNo = this.compositeArticleUtils.getCompositeArticleNo(articleContainer);
    }
    return articlePosition + LabelEditorTabComponent.SELECTED_CELLS_DESCRIPTION_POSITION_SEPARATOR + articleNo;
  }

  public generateGroupedBySeparateLabeling(connectionCubes: Connection[]): ITableProvider[] {
    const tableProviders: ITableProvider[] = [];
    const separateLabelingConnectionsMap: Map<string, Connection[]> = this.getSeparateLabelingConnectionsMap(connectionCubes);
    if (separateLabelingConnectionsMap.size > 0) {
      const separateLabelingConnectionCubes: Connection[] = [].concat(...Array.from(separateLabelingConnectionsMap.values()));
      ArrayUtils.removeAllItemsInArray(connectionCubes, separateLabelingConnectionCubes);
      separateLabelingConnectionsMap.forEach((connectionCubes: Connection[], markingCarrierUsageId: string) => {
        const tableProvider: ITableProvider = this.generate(connectionCubes, TableProviderType.SEPARATE_LABELING);
        this.finalizeSeparateLabelingTableProviderGeneration(tableProvider, markingCarrierUsageId);
        tableProviders.push(tableProvider);
      });
    }
    return tableProviders;
  }

  private getSeparateLabelingConnectionsMap(connectionCubes: Connection[]): Map<string, Connection[]> {
    const connectionsMap = new Map<string, Connection[]>();
    for (const connectionCube of connectionCubes) {
      const backwardUsageId: string = ArticleUsageUtils.getFirstBackwardConnectedArticleUsageId(connectionCube.articleContainer.articleUsage);
      const backwardArticleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(backwardUsageId);
      if (backwardArticleContainer) {
        const backwardArticle: ArticleVO = backwardArticleContainer.article;
        if (ArticleUtils.isMarkerCarrierWithSeparateLabeling(backwardArticle)) {
          if (connectionsMap.has(backwardArticleContainer.articleUsage.id)) {
            connectionsMap.get(backwardArticleContainer.articleUsage.id).push(connectionCube);
          } else {
            connectionsMap.set(backwardArticleContainer.articleUsage.id, [connectionCube]);
          }
        }
      }
    }
    return connectionsMap;
  }

  private finalizeSeparateLabelingTableProviderGeneration(tableProvider: ITableProvider, markingCarrierUsageId: string): void {
    const markingCarrierArticleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(markingCarrierUsageId);
    const markingCarrierPosition: string = this.presenter.getPositionLabelByArticleUsageId(markingCarrierUsageId);
    const markingCarrierArticleNo: string = markingCarrierArticleContainer.article.articleNo;
    tableProvider.separateLabelingPositionNumber = markingCarrierPosition;
    tableProvider.separateLabelingArticleNumber = markingCarrierArticleNo;
    tableProvider.tabTooltipText = this.articleToolTipTextUtils.createToolTipText(
      markingCarrierArticleContainer.article,
      markingCarrierArticleContainer.articleUsage
    );
    tableProvider.tabLabel = {
      label: markingCarrierPosition + ') ' + markingCarrierArticleNo,
    };
  }

  public generateGroupedByLabelingCategory(connectionCubes: Connection[]): ITableProvider[] {
    const tableProviders: ITableProvider[] = [];
    const labelingCategoryConnectionsMap: Map<string, Connection[]> = this.getLabelingCategoryConnectionsMap(connectionCubes);
    labelingCategoryConnectionsMap.forEach((connectionCubes: Connection[], labelingCategory: string) => {
      const type: TableProviderType = labelingCategory ? TableProviderType.LABELING_CATEGORY : TableProviderType.DEFAULT;
      const tableProvider: ITableProvider = this.generate(connectionCubes, type);
      tableProvider.tabLabel = this.getLabelingCategoryTabLabel(tableProvider, labelingCategory);
      tableProviders.push(tableProvider);
    });
    return tableProviders;
  }

  private getLabelingCategoryConnectionsMap(connectionCubes: Connection[]): Map<string, Connection[]> {
    const connectionsMap = new Map<string, Connection[]>();
    for (const connectionCube of connectionCubes) {
      const category: string = ArticleUtils.getLabelingCategory(connectionCube.article);
      if (connectionsMap.has(category)) {
        connectionsMap.get(category).push(connectionCube);
      } else {
        connectionsMap.set(category, [connectionCube]);
      }
    }
    return connectionsMap;
  }

  private getLabelingCategoryTabLabel(tableProvider: ITableProvider, labelingCategory: string): TableProviderTabLabel {
    const minPosition: number = tableProvider.getMinPositionOnRail();
    const maxPosition: number = tableProvider.getMaxPositionOnRail();
    const positionInfo: string = minPosition === maxPosition ? '#' + minPosition : '#' + minPosition + '...' + maxPosition;
    const tabLabel: TableProviderTabLabel = {
      label: positionInfo,
    };
    if (LocaleUtils.getLocalizedLabelingCategory(this.localResourceManager, labelingCategory)) {
      tabLabel.labelingCategoryTranslateKey = LocaleUtils.getLabelingCategoryTranslateKey(labelingCategory);
    }
    return tabLabel;
  }

  private createColumnHeaderTooltips(rows: ISpanData[][]): string[] {
    let header: string[] = [];
    const spanDatas: ISpanData[] = this.getSpanDatasForHeader(rows);

    try {
      for (const spanData of spanDatas) {
        const articleContainer: ArticleContainer = this.findArticleContainerForHeader(spanData);
        const spanDataHeader = !articleContainer
          ? null
          : this.articleToolTipTextUtils.createToolTipText(articleContainer.article, articleContainer.articleUsage);
        header.push(spanDataHeader);
      }
    } catch (e) {
      header = null;
    }
    return header;
  }

  private getSpanDatasForHeader(rows: ISpanData[][]): ISpanData[] {
    const spanDatas: ISpanData[] = [];

    let maxRowLength: number = 0;
    rows.forEach((row) => {
      if (row.length > maxRowLength) {
        maxRowLength = row.length;
      }
    });

    for (let i = 0; i < maxRowLength; i++) {
      let columnSpanData: ISpanData = null;
      for (const row of rows) {
        const spanData: ISpanData = row[i];
        if ((isConnectionSpanData(spanData) && !spanData.parent) || (isArticleSpanData(spanData) && spanData.isEndPlate)) {
          columnSpanData = spanData;
          break;
        }
      }
      spanDatas.push(columnSpanData);
    }

    return spanDatas;
  }

  private findArticleContainerForHeader(spanData: ISpanData): ArticleContainer {
    let targetArticleContainer: ArticleContainer = null;
    if (isConnectionSpanData(spanData)) {
      let articleContainer: ArticleContainer = this.getRelatedContainerOnRail(spanData.connection);
      if (!articleContainer) {
        const container: ArticleContainer = spanData.connection.articleContainer;
        articleContainer = this.articleContainerUtils.getBackwardArticleContainerOnRailRecursively(container);
      }
      // The article might not be on a mounting rail -> use the article container of the connection
      if (!articleContainer) {
        articleContainer = spanData.connection.articleContainer;
      }
      targetArticleContainer = articleContainer;
    } else if (isArticleSpanData(spanData)) {
      targetArticleContainer = this.containerService.findArticleContainerInGlobal(spanData.articleUsageId);
    }
    return targetArticleContainer;
  }

  private createSpanDataInCorridor(
    cubesInCorridor: Connection[],
    baserowDistance: number,
    cubeIdToEndPlateSpacing: Dictionary<string, EndPlateFormat>
  ): ISpanData[][] {
    const corridorSpanDatas: ISpanData[][] = [];
    for (const connectionCube of cubesInCorridor) {
      const connectionSpanDatas: ISpanData[][] = this.createSpanDataByConnection(connectionCube, baserowDistance, cubeIdToEndPlateSpacing);
      TableProviderUtils.mergeSpanDatas(corridorSpanDatas, connectionSpanDatas);
    }
    return corridorSpanDatas;
  }

  private createSpanDataByConnection(
    connectionCube: Connection,
    baserowDistance: number,
    cubeIdToEndPlateSpacing: Dictionary<string, EndPlateFormat>
  ): ISpanData[][] {
    const mergedCubes: Connection[] = this.labelingUtils.findNextMergedTextureConnections(connectionCube);
    if (mergedCubes.length === 0) {
      return [];
    }

    const isDynamicLabelingArticle: boolean = this.isConnectionOfDynamicLabelingArticle(connectionCube);

    // to be sure that texture-usage is created for the given connection
    if (!connectionCube.metaElementUsage) {
      LabelingUtils.applyTextureMetaElementUsageIfNotExists(connectionCube, this.projectArticleService, this.projectMetaElementService);
    }

    let connectionSpanDatas: IConnectionSpanData[][] = [];

    const textureUsage: TextureAreaUsageVO = connectionCube.metaElementUsage as TextureAreaUsageVO;
    const textValue: TextValueVO = textureUsage.textValue;
    if (textValue instanceof SimpleTextValueVO) {
      const simpleTextValue: SimpleTextValueVO = textValue as SimpleTextValueVO;
      connectionSpanDatas = this.createSpanDataBySimpleTextValue(
        simpleTextValue,
        baserowDistance,
        mergedCubes,
        cubeIdToEndPlateSpacing,
        isDynamicLabelingArticle
      );
    } else if (textValue instanceof TableTextValueVO) {
      const tableValue: TableTextValueVO = textValue as TableTextValueVO;
      connectionSpanDatas = this.createSpanDataByTableTextValue(
        tableValue,
        baserowDistance,
        mergedCubes,
        cubeIdToEndPlateSpacing,
        isDynamicLabelingArticle
      );
    }

    return this.fillEndPlateSpanDatas(connectionSpanDatas, cubeIdToEndPlateSpacing);
  }

  private fillEndPlateSpanDatas(
    connectionSpanDatas: IConnectionSpanData[][],
    cubeIdToEndPlateSpacing: Dictionary<string, EndPlateFormat>
  ): ISpanData[][] {
    if (ArrayUtils.isEmpty(connectionSpanDatas) || cubeIdToEndPlateSpacing.isEmpty()) {
      return connectionSpanDatas;
    }

    const tableWithEndPlates: ISpanData[][] = [];

    const firstRow: IConnectionSpanData[] = connectionSpanDatas[0]; // assumed amount of columns is the same for all rows created by merged connections
    const colCount: number = firstRow.length;
    // iterate over connections (= columns) and add additional end-plate span-datas for each row of the connection
    for (let colIndex: number = 0; colIndex < colCount; colIndex++) {
      const connectionSpanData: IConnectionSpanData = firstRow[colIndex];
      const endPlateFormat: EndPlateFormat = cubeIdToEndPlateSpacing.getValue(connectionSpanData.connectionUid);
      if (endPlateFormat && !ArrayUtils.isEmpty(endPlateFormat.endPlates)) {
        const connectionTable: ISpanData[][] = this.fillEndPlateSpanDatasByConnection(connectionSpanDatas, colIndex, endPlateFormat.endPlates);
        TableProviderUtils.mergeSpanDatas(tableWithEndPlates, connectionTable);
      } else {
        // adding of end-plate span-datas is not needed - just copy connection column
        const connectionColumn: ISpanData[] = [];
        for (let rowIndex: number = 0; rowIndex < connectionSpanDatas.length; rowIndex++) {
          const spanData: ISpanData = connectionSpanDatas[rowIndex][colIndex];
          connectionColumn.push(spanData);
        }
        TableProviderUtils.addSpanDataColumn(tableWithEndPlates, connectionColumn);
      }
    }

    return tableWithEndPlates;
  }

  private fillEndPlateSpanDatasByConnection(
    originalConnectionTable: IConnectionSpanData[][],
    colIndex: number,
    endPlates: EndPlateInfo[]
  ): ISpanData[][] {
    const connectionTable: ISpanData[][] = [];

    // to handle parent span-datas for which row-count is already updated
    const processedParentSpanDatas: ISpanData[] = [];
    // to store parent span-datas for non-merged end-plates
    const parentEndPlateDatas: Dictionary<string, IArticleSpanData> = new Dictionary<string, IArticleSpanData>();

    const rowCount: number = originalConnectionTable.length;
    for (let rowIndex: number = 0; rowIndex < rowCount; rowIndex++) {
      const connectionSpanData: ISpanData = originalConnectionTable[rowIndex][colIndex];
      const parentSpanData: ISpanData = this.getParentSpanData(connectionSpanData);
      const isMergedWithNext: boolean = this.isSpanDataMerged(connectionSpanData, originalConnectionTable[rowIndex][colIndex + 1]);

      const connectionRow: ISpanData[] = [];
      connectionRow.push(connectionSpanData);

      const endPlateDatas: IArticleSpanData[] = this.createEndPlateSpanDatas(endPlates);
      for (const endPlateData of endPlateDatas) {
        endPlateData.labelFormat.height = parentSpanData.labelFormat.height;
        if (isMergedWithNext) {
          // end-plate placed between merged connections
          endPlateData.parent = parentSpanData;
        } else {
          if (processedParentSpanDatas.indexOf(parentSpanData) < 0) {
            endPlateData.rowSpan = parentSpanData.rowSpan;
            parentEndPlateDatas.setValue(endPlateData.articleUsageId, endPlateData);
          } else {
            endPlateData.parent = parentEndPlateDatas.getValue(endPlateData.articleUsageId);
          }
        }

        connectionRow.push(endPlateData);
      }

      if (processedParentSpanDatas.indexOf(parentSpanData) < 0) {
        processedParentSpanDatas.push(parentSpanData);
        if (isMergedWithNext) {
          parentSpanData.colSpan += endPlateDatas.length;
        }
      }

      connectionTable.push(connectionRow);
    }
    return connectionTable;
  }

  private getParentSpanData(spanData: ISpanData): ISpanData {
    if (!spanData) {
      return null;
    }

    return !spanData.parent ? spanData : spanData.parent;
  }

  private isSpanDataMerged(data1: ISpanData, data2: ISpanData): boolean {
    if (!data1 || !data2) {
      return false;
    }

    const parent1: ISpanData = this.getParentSpanData(data1);
    const parent2: ISpanData = this.getParentSpanData(data2);
    return parent1 === parent2;
  }

  private createEndPlateSpanDatas(endPlates: EndPlateInfo[]): IArticleSpanData[] {
    const spanDatas: IArticleSpanData[] = [];
    for (const endPlate of endPlates) {
      if (NumberUtils.isNumber(endPlate.width) && endPlate.width > 0) {
        const endPlateSpanData: IArticleSpanData = this.spanDataFactory.createArticleSpanData(endPlate.articleUsageId);
        endPlateSpanData.labelFormat.width = endPlate.width;
        endPlateSpanData.isEndPlate = true;

        spanDatas.push(endPlateSpanData);
      }
    }
    return spanDatas;
  }

  private isSeparationColumnNeeded(startSpanData: ISpanData, endSpanData: ISpanData): boolean {
    if (!isConnectionSpanData(startSpanData) || !isConnectionSpanData(endSpanData)) {
      return false;
    }

    const startContainer: ArticleContainer = startSpanData.connection.articleContainer;
    const endContainer: ArticleContainer = endSpanData.connection.articleContainer;
    if (startContainer.articleGUID === endContainer.articleGUID) {
      return false;
    }

    if (!ArticleUtils.isLabelingArticle(startContainer.article) || !ArticleUtils.isLabelingArticle(startContainer.article)) {
      return false;
    }

    const insertHelper: InsertArticleHelper = this.createInsertArticleHelper();
    if (insertHelper) {
      const startArticleOnRail: ArticleContainer = this.articleContainerUtils.getBackwardArticleContainerOnRailRecursively(
        startSpanData.connection.articleContainer,
      );
      const endArticleOnRail: ArticleContainer = this.articleContainerUtils.getBackwardArticleContainerOnRailRecursively(
        endSpanData.connection.articleContainer,
      );
      return !this.terminalBlockManager.isRightNeighborWithoutGap(startArticleOnRail, endArticleOnRail, insertHelper);
    }

    return true;
  }

  private createSeparationSpanData(rowCount: number, format: SeparationFormat, isEndPlate: boolean): ISpanData[] {
    let parentSpanData: ISpanData = null;
    const separationColumn: ISpanData[] = [];
    for (let i: number = 0; i < rowCount; i++) {
      const separationSpanData: IArticleSpanData = this.spanDataFactory.createArticleSpanData(null);
      separationSpanData.labelFormat.width = format.width;
      separationSpanData.labelFormat.height = format.height;
      separationSpanData.labelFormat.backgroundColor = format.backgroundColor;
      separationSpanData.isEndPlate = isEndPlate;
      // set maxMultilineCount in order to avoid applying of additional insets to separation cells
      // see LabelEditorUtils.calculateCellInsets
      separationSpanData.maxMultilineCount = format.maxMultilineCount;
      if (parentSpanData) {
        separationSpanData.parent = parentSpanData;
      } else {
        separationSpanData.rowSpan = rowCount;
        parentSpanData = separationSpanData;
      }

      separationColumn.push(separationSpanData);
    }
    return separationColumn;
  }

  private createSpanDataBySimpleTextValue(
    simpleTextValue: SimpleTextValueVO,
    totalRowSpan: number,
    mergedCubes: Connection[],
    cubeIdToEndPlateSpacing: Dictionary<string, EndPlateFormat>,
    isDynamicLabelingArticle: boolean
  ): IConnectionSpanData[][] {
    // create dummy table-data with single item to prepare span-datas
    const item: TableTextItemVO = new TableTextItemVO();
    item.value = simpleTextValue;
    item.rowSpan = totalRowSpan;
    item.colSpan = mergedCubes.length;

    const row: TableTextRowVO = new TableTextRowVO();
    row.items.push(item);

    const tableValue: TableTextValueVO = new TableTextValueVO();
    tableValue.rows.push(row);

    return this.createSpanDataByTableTextValue(tableValue, totalRowSpan, mergedCubes, cubeIdToEndPlateSpacing, isDynamicLabelingArticle);
  }

  private createSpanDataByTableTextValue(
    tableTextValue: TableTextValueVO,
    totalRowSpan: number,
    mergedCubes: Connection[],
    cubeIdToEndPlateSpacing: Dictionary<string, EndPlateFormat>,
    isDynamicLabelingArticle: boolean
  ): IConnectionSpanData[][] {
    // use storage of connection-formats to avoid duplicated calculations during creation of multiple span-datas for the same connection
    const connectionFormats: Dictionary<string, ConnectionFormat> = new Dictionary<string, ConnectionFormat>();
    const spanDatas: IConnectionSpanData[][] = []; // list of span datas in rows

    const tableRowCount: number = tableTextValue.rows.length > 0 ? tableTextValue.rows.length : 1;
    const rowSpanStep: number = totalRowSpan / tableRowCount; // how much span-data-rows have to be created for each actual row

    const totalColSpan: number = mergedCubes.length;
    // create span datas for each row
    for (let rowIndex: number = 0; rowIndex < totalRowSpan; rowIndex++) {
      // in case of 4-row label-strip (total row-count is 12) parent row-indices are: 0, 3, 6, 9
      const tableRow: TableTextRowVO = TableProviderUtils.findRowByIndex(tableTextValue, rowIndex, rowSpanStep);
      // create span datas for each column in specific row (assuming total col-span is equal to amount of merged connections)
      for (let colIndex: number = 0; colIndex < totalColSpan; colIndex++) {
        const mergedCube: Connection = mergedCubes[colIndex];
        if (!connectionFormats.containsKey(mergedCube.uid)) {
          connectionFormats.setValue(mergedCube.uid, this.createConnectionFormat(mergedCube, cubeIdToEndPlateSpacing));
        }

        const connectionSpanData: IConnectionSpanData = this.createConnectionSpanData(mergedCube, connectionFormats.getValue(mergedCube.uid));
        const rowItem: TableTextItemVO = TableProviderUtils.findRowItemByIndex(tableRow, colIndex);
        if (rowItem) {
          const parentRowIndex: number = TableProviderUtils.getRowIndex(tableTextValue, tableRow, rowSpanStep);
          const parentColIndex: number = TableProviderUtils.getRowItemIndex(tableRow, rowItem);
          if (parentRowIndex === rowIndex) {
            connectionSpanData.terminalPositionOnRail = this.getSpanDataTerminalPositionOnRail(connectionSpanData);
          }
          if (parentRowIndex === rowIndex && parentColIndex === colIndex) {
            // actual text (as well as row-/col-span) has to be set only to the main/parent span-data
            this.fillParentSpanData(connectionSpanData, rowItem.value as SimpleTextValueVO, rowSpanStep, rowItem.colSpan);
          } else {
            // other span-datas contain just a reference to the parent span-data
            connectionSpanData.parent = spanDatas[parentRowIndex][parentColIndex];
          }
        }

        this.checkSizeForConnectionSpanData(connectionSpanData, isDynamicLabelingArticle);
        TableProviderUtils.addSpanData(spanDatas, connectionSpanData, rowIndex, colIndex);
      }
    }

    spanDatas.forEach((rowDatas: IConnectionSpanData[]) => {
      this.checkSizeForMergedSpanDatas(rowDatas);
    });
    return spanDatas;
  }

  private getSpanDataTerminalPositionOnRail(spanData: IConnectionSpanData): number {
    const articleContainer: ArticleContainer = this.findArticleContainerForHeader(spanData);
    if (!articleContainer) {
      return null;
    }
    const articlePositionString: string = this.presenter.getPositionLabelByArticleUsageId(articleContainer.articleUsage.id);
    const articlePosition: number = Number(articlePositionString.split('.')[0]);
    return NumberUtils.isNumber(articlePosition) && articlePosition > 0 ? articlePosition : null;
  }

  private createConnectionSpanData(connection: Connection, format: ConnectionFormat): IConnectionSpanData {
    const connectionSpanData: IConnectionSpanData = this.spanDataFactory.createConnectionSpanData(connection);
    connectionSpanData.compositeArticle = this.compositeArticleInfoUtils.getCompositeArticleFromContainer(connection.articleContainer);
    connectionSpanData.defaultTextParameters = this.defaultTextParametersManager.getDefaultTextParameterByArticleContainer(
      connection.articleContainer
    );

    let font: string = ConfiguratorParameters.labeling.defaultLabelingFontName;

    if (!ArticleUtils.isLabelingArticle(connection.article) && this.articleContainerUtils.isOrIsPartOfMcsGdsLtl(connection.articleContainer)) {
      font = ConfiguratorParameters.labeling.dinFontNameMiddle;
    }

    connectionSpanData.font = font;
    connectionSpanData.labelFormat.width = format.width;
    connectionSpanData.labelFormat.height = format.height;
    connectionSpanData.labelFormat.backgroundColor = format.backgroundColor;
    connectionSpanData.labelFormat.borderColor = format.borderColor;
    connectionSpanData.isMergeWithNext = format.isMergeWithNext;
    connectionSpanData.spacingToNextSpanDataInRow = format.spacingToNextSpanDataInRow;
    return connectionSpanData;
  }

  private createConnectionFormat(connection: Connection, cubeIdToEndPlateSpacing: Dictionary<string, EndPlateFormat>): ConnectionFormat {
    const connectionFormat: ConnectionFormat = new ConnectionFormat();
    const connectionInfo: TextureConnectionInfo = this.getConnectionInfo(connection);
    connectionFormat.width = connectionInfo.connectionWidth;
    connectionFormat.height = connectionInfo.connectionHeight;
    connectionFormat.backgroundColor = this.getArticleHexColor(connection);
    if (this.labelingUtils.isTextureCubePartOfDefaultLabeling(connection)) {
      if (IsProjectType.mcsGds(this.projectModel.currentProject)) {
        connectionFormat.borderColor = LabelFormat.DEFAULT_LABELING_BORDER_COLOR;
      }
    }
    if (cubeIdToEndPlateSpacing.containsKey(connection.uid)) {
      const endPlateFormat: EndPlateFormat = cubeIdToEndPlateSpacing.getValue(connection.uid);
      connectionFormat.isMergeWithNext = true;
      connectionFormat.spacingToNextSpanDataInRow = endPlateFormat.totalSpacing;
    }
    return connectionFormat;
  }

  private getConnectionInfo(connection: Connection): TextureConnectionInfo {
    const articleContainer: ArticleContainer = connection.articleContainer;
    const textureInfo: TextureConnectionInfo = articleContainer.findTextureConnectionInfo(connection.connectionName);
    // info can be null in case texture-connection is on some non-label article (e.g. directly on mcs article)
    return !textureInfo ? new TextureConnectionInfo(connection.connectionName, connection.compositionRectangle, 0, connection.type) : textureInfo;
  }

  private getArticleHexColor(connection: Connection): string {
    let colorValue: string = LabelFormat.DEFAULT_BACKGROUND_COLOR;
    const articleContainer: ArticleContainer = connection.articleContainer;
    if (ArticleUtils.isLabelingArticle(articleContainer.article)) {
      // background color is set only for connections on labeling articles (like label-strip, WMB)
      // direct printing (e.g. texture-connections on mcs-articles) are ignored
      const articleMeshes: Mesh[] = MeshUtils.findGeometryMeshes(articleContainer.model);
      let color: Color = null;
      for (const mesh of articleMeshes) {
        if (mesh.material && mesh.material.hasOwnProperty('color') && mesh.material['color'] instanceof Color) {
          color = mesh.material['color'] as Color;
          break;
        }
      }
      if (color) {
        colorValue = '#' + color.getHexString();
      }
    }
    return colorValue;
  }

  private fillParentSpanData(spanData: IConnectionSpanData, value: SimpleTextValueVO, rowSpan: number, colSpan: number): void {
    if (spanData.editable) {
      spanData.data = value.text;
      spanData.labelFormat.direction = value.rotation;
      spanData.labelFormat.verticalAlign = TextureUtils.getVerticalAlignByIndex(value.vAlignment);
    }
    if (value instanceof CompressionSimpleTextValueVO) {
      spanData.compression = (value as CompressionSimpleTextValueVO).compression;
      spanData.autoFormatByTextCheck = this.isAutoFormatByTextCheck(value as CompressionSimpleTextValueVO);
    }
    spanData.rowSpan = rowSpan;
    spanData.colSpan = colSpan;
  }

  private isAutoFormatByTextCheck(textValue: CompressionSimpleTextValueVO): boolean {
    const autoFontSize: boolean = textValue.autoFontSize;
    const autoCompression: boolean = textValue.autoCompression;
    return autoFontSize || autoCompression;
  }

  private isConnectionOfDynamicLabelingArticle(connection: Connection): boolean {
    let isDynamicLabelingArticle: boolean = false;
    if (connection && connection.articleContainer) {
      const articleContainer: ArticleContainer = connection.articleContainer;
      isDynamicLabelingArticle = ArticleUtils.isDynamicLabelingArticle(articleContainer.article);
    }
    return isDynamicLabelingArticle;
  }

  private checkSizeForConnectionSpanData(spanData: IConnectionSpanData, isDynamicLabelingArticle: boolean): void {
    if (spanData.connection.metaElementVO instanceof TextureAreaVO) {
      const textCheckClass: string = PropertyUtils.getStringPropertyValue(
        spanData.connection.metaElementVO.behaviorProperties,
        MetaElementBehaviorPropertyDef.TEXT_CHECK_CLASS
      );
      let maxMultilineCount: number = PropertyUtils.getIntPropertyValue(
        spanData.connection.metaElementVO.behaviorProperties,
        MetaElementBehaviorPropertyDef.ALLOWED_MULTILINES
      );
      if (NumberUtils.isNotNumber(maxMultilineCount)) {
        // default value of 1 (instead of NaN) is needed to handle different width of cells in the same row by label-editor correctly
        maxMultilineCount = 1;
      }
      spanData.maxMultilineCount = maxMultilineCount;
      if (textCheckClass) {
        spanData.textCheckClass = textCheckClass;
        const spacingData: TextCheckSpacingData = this.labelingUtils.getTextCheckSpacingDataByTextureCube(spanData.connection);
        if (spacingData) {
          spanData.cellLabelSizeInfos = this.textSizeCheckManager.getTextSizeCheckWithFallback(textCheckClass, spacingData, isDynamicLabelingArticle);
          spanData.spacing = spacingData.getAverageSpacing();
        }
      }
    }
  }

  private checkSizeForMergedSpanDatas(spanDataArray: ISpanData[]): void {
    if (spanDataArray.length > 1) {
      for (const spanData of spanDataArray) {
        if (spanData.colSpan > 1) {
          const startMergeIndex: number = spanDataArray.indexOf(spanData);
          let mergedSpanDatas: ISpanData[] = spanDataArray.slice(startMergeIndex, startMergeIndex + spanData.colSpan);
          mergedSpanDatas = mergedSpanDatas.filter((sd) => {
            return isConnectionSpanData(sd);
          });
          const averageSpacing: number = LabelingUtils.calculateAverageSpacing(mergedSpanDatas);
          spanData.cellLabelSizeInfos = this.textSizeCheckManager.getTextSizeCheckWithDecidingSpacing(spanData.textCheckClass, averageSpacing);
        }
      }
    }
  }

  private getEndPlateSpacingAfterCubes(
    cubes: Connection[],
    selectedConnectionCubes: Connection[],
    insertArticleHelper: InsertArticleHelper
  ): Dictionary<string, EndPlateFormat> {
    const cubeIdToEndPlateSpacing: Dictionary<string, EndPlateFormat> = new Dictionary<string, EndPlateFormat>();
    if (ArrayUtils.isEmpty(cubes) || ArrayUtils.isEmpty(selectedConnectionCubes) || !insertArticleHelper) {
      return cubeIdToEndPlateSpacing;
    }

    let startArticleContainer: ArticleContainer = null;
    let endArticleContainer: ArticleContainer = null;
    for (const connectionCube of cubes) {
      if (selectedConnectionCubes.indexOf(connectionCube) === -1) {
        continue;
      }

      let nextSelectedCube: Connection = cubes[cubes.indexOf(connectionCube) + 1];
      if (selectedConnectionCubes.indexOf(nextSelectedCube) < 0) {
        nextSelectedCube = null;
      }

      const articleContainer: ArticleContainer = this.containerService.getArticleContainer(connectionCube);
      if (!articleContainer || !articleContainer.article) {
        continue;
      }

      // create meta element usage
      if (!connectionCube.metaElementUsage) {
        LabelingUtils.applyTextureMetaElementUsageIfNotExists(connectionCube, this.projectArticleService, this.projectMetaElementService);
      }

      let mergeWithNext: boolean = true;
      const mergedCubes: Connection[] = this.labelingUtils.findNextMergedTextureConnections(connectionCube);

      if (nextSelectedCube) {
        mergeWithNext = this.isMergeWithNextAvailable(connectionCube, nextSelectedCube);
      }

      for (let index: number = 0; index < mergedCubes.length; index++) {
        const mergedCube: Connection = mergedCubes[index];
        let nextCube: Connection = null;
        if (index === mergedCubes.length - 1) {
          if (mergeWithNext) {
            nextCube = nextSelectedCube;
          }
        } else {
          nextCube = mergedCubes[index + 1];
        }

        if (this.isMergeWithNextAvailable(mergedCube, nextCube)) {
          startArticleContainer = !endArticleContainer ? this.getRelatedContainerOnRail(mergedCube) : endArticleContainer;
          endArticleContainer = this.getRelatedContainerOnRail(nextCube);

          const endPlateFormat: EndPlateFormat = new EndPlateFormat();
          if (this.isEndPlatePlacedBeforeArticleConnection(articleContainer.textConnections, nextCube.connectionName)) {
            const endPlates: EndPlateInfo[] = this.getEndPlatesBetweenArticles(startArticleContainer, endArticleContainer, insertArticleHelper);
            endPlateFormat.endPlates = endPlates;
          }
          cubeIdToEndPlateSpacing.setValue(mergedCube.uid, endPlateFormat);
        }
      }
    }
    return cubeIdToEndPlateSpacing;
  }

  public isMergeWithNextAvailable(connection: Connection, nextConnection: Connection): boolean {
    if (!connection || !nextConnection) {
      return false;
    }
    const articleContainer: ArticleContainer = this.containerService.getArticleContainer(connection);
    const nextArticleContainer: ArticleContainer = this.containerService.getArticleContainer(nextConnection);
    if (articleContainer === nextArticleContainer && ArticleUtils.isLabelingArticle(articleContainer.article)) {
      return true;
    }
    // merging texture connection on different labeling articles is not allowed
    if (ArticleUtils.isLabelingArticle(articleContainer.article) || ArticleUtils.isLabelingArticle(articleContainer.article)) {
      return false;
    }

    return this.isDirectPrintingMergeAvailable(connection, nextConnection);
  }

  private isDirectPrintingMergeAvailable(connection: Connection, nextConnection: Connection): boolean {
    // check if some of connections is denied to merge by property
    if (
      PropertyUtils.getIntPropertyValue(connection.metaElementVO.behaviorProperties, MetaElementBehaviorPropertyDef.IS_MERGE_FOR_AREA_DENIED) ||
      PropertyUtils.getIntPropertyValue(connection.metaElementVO.behaviorProperties, MetaElementBehaviorPropertyDef.IS_MERGE_FOR_AREA_DENIED)
    ) {
      return false;
    }

    if (
      !MathUtils.equalsNumber(connection.compositionRectangle.height, nextConnection.compositionRectangle.height) ||
      !MathUtils.isRectanglesNormalsEqual(connection.compositionRectangle, nextConnection.compositionRectangle)
    ) {
      return false;
    }

    // check if texture connection are on the same plane (vertically aligned)
    if (!MathUtils.isRectanglesVerticallyAligned(connection.compositionRectangle, nextConnection.compositionRectangle)) {
      return false;
    }

    // check distance between texture connection
    const distance: number = MathUtils.calculateDistanceBetweenRectangles(connection.compositionRectangle, nextConnection.compositionRectangle);
    if (!MathUtils.equalsNumber(distance, 0, 0.2)) {
      return false;
    }

    return true;
  }

  private isEndPlatePlacedBeforeArticleConnection(textConnections: TextureConnectionInfo[], connectionName: string): boolean {
    if (!textConnections || textConnections.length === 0 || !connectionName) {
      return false;
    }

    for (const info of textConnections) {
      if (info.id === connectionName) {
        return info.distanceToPreviousConnection > 0;
      }
    }
    return false;
  }

  private getEndPlatesBetweenArticles(
    startArticleContainer: ArticleContainer,
    endArticleContainer: ArticleContainer,
    insertArticleHelper: InsertArticleHelper
  ): EndPlateInfo[] {
    if (!startArticleContainer || !endArticleContainer || !insertArticleHelper) {
      return [];
    }

    const endPlates: EndPlateInfo[] = [];
    const containers: ArticleContainer[] = insertArticleHelper.getConnectedArticlesBetweenContainers(startArticleContainer, endArticleContainer);
    for (const container of containers) {
      if (ArticleUtils.isEndPlateArticle(container.article)) {
        const endPlateInfo: EndPlateInfo = new EndPlateInfo(container.articleGUID);

        const spacingData: TextCheckSpacingData = LabelingUtils.createTextCheckDataByArticle(container.article, null);
        const spacing: number = spacingData.getAverageSpacing();
        if (NumberUtils.isNumber(spacing)) {
          endPlateInfo.spacing = spacing;
        }

        const line: AdvancedLine = insertArticleHelper.getArticleLine(container);
        if (line) {
          endPlateInfo.width = line.length;
        }

        endPlates.push(endPlateInfo);
      }
    }
    return endPlates;
  }

  private getRelatedContainerOnRail(cube: Connection): ArticleContainer {
    let relatedContainerOnRail: ArticleContainer = null;
    const startBackwardReferenceArticleUsageId: string = ReferenceArticleUsageUtils.getBackwardReferenceArticleUsageId(
      cube.articleContainer.articleUsage,
      cube.connectionName
    );
    if (!startBackwardReferenceArticleUsageId) {
      const backwardUsageId: string = LabelingUtils.findBackwardUsageIdByRelativeConnection(cube.articleContainer, cube.connectionName);
      const backwardArticleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(backwardUsageId);
      relatedContainerOnRail = this.articleContainerUtils.getBackwardArticleContainerOnRailRecursively(backwardArticleContainer);
    } else {
      relatedContainerOnRail = this.containerService.findArticleContainerInGlobal(startBackwardReferenceArticleUsageId);
    }

    return relatedContainerOnRail;
  }

  private getTextureCubesBySelectedConnections(selectedConnectionCubes: Connection[]): Connection[] {
    if (!selectedConnectionCubes || selectedConnectionCubes.length === 0) {
      return [];
    }

    const processedArticleGuids: string[] = [];
    let textureCubes: Connection[] = [];
    let articleContainer: ArticleContainer = null;
    for (const cube of selectedConnectionCubes) {
      articleContainer = this.containerService.getArticleContainer(cube);
      if (!articleContainer || !articleContainer.articleGUID || processedArticleGuids.indexOf(articleContainer.articleGUID) >= 0) {
        continue;
      }

      if (articleContainer.article) {
        const articleTextureCubes: Connection[] = ConnectionInfoUtils.getTextConnectionCubesByMetaElements(
          articleContainer,
          articleContainer.article.metaElements
        );
        textureCubes = textureCubes.concat(articleTextureCubes);
      }
      processedArticleGuids.push(articleContainer.articleGUID);
    }
    return textureCubes;
  }

  private createInsertArticleHelper(): InsertArticleHelper {
    const mountingRailUsages: ArticleUsageVO[] = this.projectArticleService.findArticleUsagesByType(ArticleTypeConstants.TYPE_MOUNTING_RAIL);
    if (!mountingRailUsages || mountingRailUsages.length === 0) {
      return null;
    }

    const mountingRailContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal((mountingRailUsages[0] as ArticleUsageVO).id);
    return this.insertArticleHelperFactory.createInstanceByRangeOnLineArticleContainer(mountingRailContainer);
  }

  private addBlankAndSeparationCells(rows: ISpanData[][], lineSpans: number[]): ISpanData[][] {
    let groupedRows: RowSpanData[] = this.groupRowsByLineSpans(rows, lineSpans);
    if (lineSpans.length > 1) {
      groupedRows = this.addBlankCells(groupedRows);
      groupedRows = this.addBlankCellsForMultipleLabelsInSamePosition(groupedRows);
      groupedRows = this.removeBlankColumns(groupedRows);
    }
    groupedRows = this.addSeparationCells(groupedRows);
    return [].concat(...groupedRows.map((row: RowSpanData) => row.spanData));
  }

  private groupRowsByLineSpans(rows: ISpanData[][], lineSpans: number[]): RowSpanData[] {
    const groupedRows: RowSpanData[] = [];
    for (let i = 0; i < lineSpans.length; i++) {
      const start = i === 0 ? 0 : lineSpans.slice(0, i).reduce((sum: number, current: number) => sum + current, 0);
      const end = start + lineSpans[i];
      groupedRows.push({
        spanData: rows.slice(start, end),
      });
    }
    return groupedRows;
  }

  private addBlankCells(groupedRows: RowSpanData[]): RowSpanData[] {
    const result: RowSpanData[] = [];
    const maxRowLength: number = this.getMaxRowLength(groupedRows);
    const minPosition: number = this.getGroupedRowsMinPosition(groupedRows);

    for (let colIndex = 0; colIndex < maxRowLength; colIndex++) {
      const columns: ISpanData[][] = groupedRows.map((row: RowSpanData) => TableProviderUtils.getSpanDataColumn(row.spanData, colIndex));
      columns.forEach((column: ISpanData[], rowIndex: number) => {
        const currentSpanData: ISpanData = column[0];
        const previousSpanData: ISpanData = groupedRows[rowIndex].spanData[0][colIndex - 1];
        const currentPosition: number =
          currentSpanData && isConnectionSpanData(currentSpanData) ? currentSpanData.terminalPositionOnRail : Number.NaN;
        const previousPosition: number =
          previousSpanData && isConnectionSpanData(previousSpanData) ? previousSpanData.terminalPositionOnRail : Number.NaN;

        if (colIndex === 0) {
          result[rowIndex] = {
            spanData: [],
          };
          if (currentPosition > minPosition) {
            this.addBlankCellsToRow(result[rowIndex], groupedRows[rowIndex], currentPosition - minPosition);
          }
        } else if (currentPosition && previousPosition) {
          this.addBlankCellsToRow(result[rowIndex], groupedRows[rowIndex], currentPosition - previousPosition - 1);
        }
        TableProviderUtils.addSpanDataColumn(result[rowIndex].spanData, column);
      });
    }

    return result;
  }

  private getMaxRowLength(groupedRows: RowSpanData[]): number {
    let maxRowLength: number = 0;
    groupedRows.forEach((row: RowSpanData) => {
      const rowLength: number = row.spanData[0].length;
      if (rowLength > maxRowLength) {
        maxRowLength = rowLength;
      }
    });
    return maxRowLength;
  }

  private getGroupedRowsMinPosition(groupedRows: RowSpanData[]): number {
    let minPosition: number;
    groupedRows.forEach((row: RowSpanData) => {
      const firstSpanData: ISpanData = row.spanData[0][0];
      if (isConnectionSpanData(firstSpanData) && (!minPosition || firstSpanData.terminalPositionOnRail < minPosition)) {
        minPosition = firstSpanData.terminalPositionOnRail;
      }
    });
    return minPosition;
  }

  private addBlankCellsToRow(targetRow: RowSpanData, sourceRow: RowSpanData, count: number): void {
    this.addBlankOrSeparationCellsToRow(targetRow, sourceRow, count, false);
  }

  private insertBlankCellsToRow(row: RowSpanData, count: number, insertPosition: number): void {
    this.addBlankOrSeparationCellsToRow(row, row, count, false, insertPosition);
  }

  private addSeparationCellsToRow(targetRow: RowSpanData, sourceRow: RowSpanData, count: number): void {
    this.addBlankOrSeparationCellsToRow(targetRow, sourceRow, count, true);
  }

  private addBlankOrSeparationCellsToRow(
    targetRow: RowSpanData,
    sourceRow: RowSpanData,
    count: number,
    isSeparationCells: boolean,
    insertPosition: number = null
  ): void {
    if (!targetRow || !targetRow.spanData || ArrayUtils.isEmpty(sourceRow.spanData) || count < 1) {
      return;
    }

    const firstRow: ISpanData[] = sourceRow.spanData[0];
    const rowCount: number = sourceRow.spanData.length;
    const maxHeight: number = Math.max(...firstRow.map((data) => data.labelFormat.height));
    const maxMultilineCount: number = Math.max(...firstRow.map((data) => data.maxMultilineCount));

    const format: SeparationFormat = new SeparationFormat();
    format.width = isSeparationCells ? SeparationFormat.DEFAULT_WIDTH : 0;
    format.height = maxHeight;
    format.maxMultilineCount = maxMultilineCount;
    format.backgroundColor = isSeparationCells ? SeparationFormat.DEFAULT_BACKGROUND_COLOR : null;

    for (let i = 1; i <= count; i++) {
      const separationColumn: ISpanData[] = this.createSeparationSpanData(rowCount, format, isSeparationCells);
      if (insertPosition) {
        TableProviderUtils.insertSpanDataColumn(targetRow.spanData, separationColumn, insertPosition);
      } else {
        TableProviderUtils.addSpanDataColumn(targetRow.spanData, separationColumn);
      }
    }
  }

  private addBlankCellsForMultipleLabelsInSamePosition(groupedRows: RowSpanData[]): RowSpanData[] {
    const result: RowSpanData[] = [];
    const groupedRowIndexes = ArrayUtils.createIntSequenceArray(0, groupedRows.length - 1);
    let maxRowLength: number = this.getMaxRowLength(groupedRows);

    for (let colIndex = 0; colIndex < maxRowLength; colIndex++) {
      const rowIndexesWithSameNeighborsPosition: number[] = [];
      const columns: ISpanData[][] = groupedRows.map((row: RowSpanData) => TableProviderUtils.getSpanDataColumn(row.spanData, colIndex));
      columns.forEach((column: ISpanData[], rowIndex: number) => {
        if (colIndex === 0) {
          result[rowIndex] = {
            spanData: [],
          };
        }
        TableProviderUtils.addSpanDataColumn(result[rowIndex].spanData, column);
        const currentSpanData: ISpanData = column[0];
        const nextSpanData: ISpanData = groupedRows[rowIndex].spanData[0][colIndex + 1];
        const currentPosition: number =
          currentSpanData && isConnectionSpanData(currentSpanData) ? currentSpanData.terminalPositionOnRail : Number.NaN;
        const nextPosition: number = nextSpanData && isConnectionSpanData(nextSpanData) ? nextSpanData.terminalPositionOnRail : Number.NaN;
        if (currentPosition && nextPosition && currentPosition === nextPosition) {
          rowIndexesWithSameNeighborsPosition.push(rowIndex);
        }
      });

      if (ArrayUtils.isNotEmpty(rowIndexesWithSameNeighborsPosition)) {
        const rowIndexesToProcess = groupedRowIndexes.filter((index) => !rowIndexesWithSameNeighborsPosition.includes(index));
        rowIndexesToProcess.forEach((rowIndex) => {
          const column: ISpanData[] = TableProviderUtils.getSpanDataColumn(groupedRows[rowIndex].spanData, colIndex);
          if (ArrayUtils.isNotEmpty(column)) {
            this.insertBlankCellsToRow(groupedRows[rowIndex], 1, colIndex + 1);
          }
        });
        maxRowLength = this.getMaxRowLength(groupedRows);
      }
    }

    return result;
  }

  private removeBlankColumns(groupedRows: RowSpanData[]): RowSpanData[] {
    const result: RowSpanData[] = [];
    const maxRowLength: number = this.getMaxRowLength(groupedRows);

    for (let colIndex = 0; colIndex < maxRowLength; colIndex++) {
      const columns: ISpanData[][] = groupedRows.map((row: RowSpanData) => TableProviderUtils.getSpanDataColumn(row.spanData, colIndex));
      const columnSpanDatas: ISpanData[] = columns.map((column: ISpanData[]) => column[0]);
      if (!columnSpanDatas.filter((spanData) => !!spanData).every((spanData) => isArticleSpanData(spanData) && !spanData.isEndPlate)) {
        columns.forEach((column: ISpanData[], rowIndex: number) => {
          if (colIndex === 0) {
            result[rowIndex] = {
              spanData: [],
            };
          }
          TableProviderUtils.addSpanDataColumn(result[rowIndex].spanData, column);
        });
      }
    }

    return result;
  }

  private addSeparationCells(groupedRows: RowSpanData[]): RowSpanData[] {
    const result: RowSpanData[] = [];
    const groupedRowIndexes = ArrayUtils.createIntSequenceArray(0, groupedRows.length - 1);
    const maxRowLength: number = this.getMaxRowLength(groupedRows);

    for (let colIndex = 0; colIndex < maxRowLength; colIndex++) {
      const processedRowIndexes: number[] = [];
      const connectionColumns: ISpanData[][] = groupedRows.map((row: RowSpanData) => TableProviderUtils.getSpanDataColumn(row.spanData, colIndex));
      connectionColumns.forEach((columns: ISpanData[], rowIndex: number) => {
        if (colIndex === 0) {
          result[rowIndex] = {
            spanData: [],
          };
        }
        TableProviderUtils.addSpanDataColumn(result[rowIndex].spanData, columns);

        const currentSpanData: ISpanData = columns[0];
        const nextSpanData: ISpanData = groupedRows[rowIndex].spanData[0][colIndex + 1];
        if (this.isSeparationColumnNeeded(currentSpanData, nextSpanData)) {
          this.addSeparationCellsToRow(result[rowIndex], groupedRows[rowIndex], 1);
          processedRowIndexes.push(rowIndex);
        }
      });
      if (ArrayUtils.isNotEmpty(processedRowIndexes)) {
        const unprocessedRowIndexes = groupedRowIndexes.filter((index) => !processedRowIndexes.includes(index));
        unprocessedRowIndexes.forEach((rowIndex) => {
          const column: ISpanData[] = TableProviderUtils.getSpanDataColumn(groupedRows[rowIndex].spanData, colIndex);
          if (ArrayUtils.isNotEmpty(column)) {
            this.addBlankCellsToRow(result[rowIndex], groupedRows[rowIndex], 1);
          }
        });
      }
    }

    return result;
  }
}
